/**
 * Modern InfiniteScroll implementation using IntersectionObserver.
 * Replaces the Metafizzy infinite-scroll library which uses deprecated 'unload' event.
 */

type EventCallback = (...args: unknown[]) => void;

export class InfiniteScroll {
    private container: HTMLElement;

    private options: {
        path: string | (() => string | undefined);
        append: string;
        button?: string;
        status?: string;
        history?: 'push' | 'replace' | false;
        scrollThreshold?: number;
        loadOnScroll?: boolean;
    };

    public loadCount = 0;

    private isLoading = false;

    private isDestroyed = false;

    private observer: IntersectionObserver | null = null;

    private sentinelElement: HTMLElement | null = null;

    private eventListeners: Map<string, EventCallback[]> = new Map();

    constructor(container: HTMLElement, options: Record<string, unknown>) {
        this.container = container;
        this.options = {
            path: options.path as string | (() => string | undefined),
            append: options.append as string,
            button: options.button as string | undefined,
            status: options.status as string | undefined,
            history: options.history as 'push' | 'replace' | false | undefined,
            scrollThreshold: (options.scrollThreshold as number) ?? 400,
            loadOnScroll: (options.loadOnScroll as boolean | undefined) ?? true,
        };

        this.init();
    }

    private init(): void {
        this.syncButtonVisibility();
        this.setupObserver();
    }

    private syncButtonVisibility(): void {
        // Never show button while loading or if destroyed
        if (this.isLoading || this.isDestroyed) {
            this.hideButton();

            return;
        }

        // Show button only when manual paging is enabled (loadOnScroll is false)
        if (this.loadOnScroll) {
            this.hideButton();
        } else {
            this.showButton();
        }
    }

    private setupObserver(): void {
        this.sentinelElement = document.createElement('div');
        this.sentinelElement.className = 'infinite-scroll-sentinel';
        this.sentinelElement.style.height = '1px';
        this.sentinelElement.style.width = '100%';
        this.container.appendChild(this.sentinelElement);

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && this.options.loadOnScroll && !this.isLoading) {
                        this.loadNextPage().catch(() => {});
                    }
                });
            },
            {
                rootMargin: `0px 0px ${this.options.scrollThreshold}px 0px`,
                threshold: 0,
            },
        );

        this.observer.observe(this.sentinelElement);

        this.setupButtonHandler();
    }

    private setupButtonHandler(): void {
        if (!this.options.button) {
            return;
        }

        const buttonContainer = document.querySelector<HTMLElement>(this.options.button);

        if (!buttonContainer) {
            return;
        }

        // Handle click on the container or any button inside
        buttonContainer.addEventListener('click', () => {
            if (!this.isLoading) {
                this.loadNextPage().catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error('InfiniteScroll: Button click load failed', error);
                });
            }
        });
    }

    private getNextPageUrl(): string | undefined {
        if (typeof this.options.path === 'function') {
            return this.options.path.call(this);
        }

        const linkElement = document.querySelector<HTMLAnchorElement>(this.options.path);

        return linkElement?.href;
    }

    private async loadNextPage(): Promise<void> {
        if (this.isLoading || this.isDestroyed) {
            return;
        }

        const url = this.getNextPageUrl();

        if (!url) {
            this.hideStatus();
            this.hideButton();

            return;
        }

        this.isLoading = true;
        this.showStatus();
        this.hideButton();
        this.emit('request', url);

        try {
            const response = await fetch(url);

            // Bail out if destroyed while waiting for fetch
            if (this.isDestroyed) {
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();

            // Bail out if destroyed while waiting for response body
            if (this.isDestroyed) {
                return;
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Bail out before any DOM mutations
            if (this.isDestroyed) {
                return;
            }

            const elementsToAppend = doc.querySelectorAll(this.options.append);
            const clonedNodes: Node[] = [];

            if (elementsToAppend.length > 0) {
                const fragment = document.createDocumentFragment();

                elementsToAppend.forEach((element) => {
                    const clone = element.cloneNode(true);
                    clonedNodes.push(clone);
                    fragment.appendChild(clone);
                });

                if (this.sentinelElement) {
                    this.container.insertBefore(fragment, this.sentinelElement);
                } else {
                    this.container.appendChild(fragment);
                }

                this.emit('append', clonedNodes);
            }

            // Bail out before updating links and history
            if (this.isDestroyed) {
                return;
            }

            this.updateNextPageLink(doc);

            if (this.options.history) {
                this.updateHistory(url, doc);
            }

            this.loadCount += 1;
            this.emit('load', clonedNodes, url);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('InfiniteScroll: Failed to load page', error);
            this.emit('error', error);
        } finally {
            this.isLoading = false;
            this.hideStatus();
            this.syncButtonVisibility();
        }
    }

    private updateNextPageLink(doc: Document): void {
        if (typeof this.options.path === 'string') {
            const newLink = doc.querySelector<HTMLAnchorElement>(this.options.path);
            const currentLink = document.querySelector<HTMLAnchorElement>(this.options.path);

            if (currentLink && newLink) {
                currentLink.href = newLink.href;
            } else if (currentLink && !newLink) {
                currentLink.remove();
            }
        }
    }

    private updateHistory(url: string, doc: Document): void {
        const title = doc.querySelector('title')?.textContent || document.title;

        if (this.options.history === 'push') {
            window.history.pushState({ infiniteScroll: true }, title, url);
        } else if (this.options.history === 'replace') {
            window.history.replaceState({ infiniteScroll: true }, title, url);
        }

        this.emit('history', title, url);
    }

    private showStatus(): void {
        if (this.options.status) {
            const status = document.querySelector<HTMLElement>(this.options.status);

            if (status) {
                status.style.display = '';
                status.classList.remove('d-none');
            }
        }
    }

    private hideStatus(): void {
        if (this.options.status) {
            const status = document.querySelector<HTMLElement>(this.options.status);

            if (status) {
                status.style.display = '';
                status.classList.add('d-none');
            }
        }
    }

    private showButton(): void {
        if (this.options.button) {
            const button = document.querySelector<HTMLElement>(this.options.button);

            if (button) {
                button.style.display = '';
                button.classList.remove('d-none');
            }
        }
    }

    private hideButton(): void {
        if (this.options.button) {
            const button = document.querySelector<HTMLElement>(this.options.button);

            if (button) {
                button.style.display = '';
                button.classList.add('d-none');
            }
        }
    }

    public on(event: string, callback: EventCallback): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }

        const listeners = this.eventListeners.get(event);

        if (listeners) {
            listeners.push(callback);
        }
    }

    private emit(event: string, ...args: unknown[]): void {
        const listeners = this.eventListeners.get(event);

        if (listeners) {
            listeners.forEach((callback) => callback(...args));
        }
    }

    public destroy(): void {
        this.isDestroyed = true;

        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        if (this.sentinelElement) {
            this.sentinelElement.remove();
            this.sentinelElement = null;
        }

        this.eventListeners.clear();
    }

    // Getter/setter for loadOnScroll to match original API
    public get loadOnScroll(): boolean {
        return this.options.loadOnScroll ?? true;
    }

    public set loadOnScroll(value: boolean) {
        this.options.loadOnScroll = value;
        this.syncButtonVisibility();
    }
}

// Make available globally
window.InfiniteScroll = InfiniteScroll as unknown as typeof window.InfiniteScroll;
